<script setup lang="ts">
import { computed, watch } from 'vue';
import {
    BDialog, BChip, BBtn,
} from '@/design-system/components';
import { useTranslation } from '@/composables';
import { useDialogStore } from '@/stores/dialogStore';
import LogList from './LogList.vue';
import type { AntennaReadingType } from '@/types/AntennaReadingType';
import type { AntennaType } from '@/types/AntennaType';

type PropsType = {
    antennaUuid?: string;
    readings: AntennaReadingType[];
    getAntennaInfo: (uuid: string) => AntennaType | undefined;
    getAntennaReadings: (uuid: string) => AntennaReadingType[];
};

const props = defineProps<PropsType>();
const { t } = useTranslation();
const dialogId = 'antenna-log-dialog';
const isOpen = defineModel<boolean>('open', { required: true, default: false });
const dialogStore = useDialogStore();

const antenna = computed(() => {
    if (!props.antennaUuid) {
        return { name: t('antennas.allAntennas'), uuid: '', reader_name: '' };
    }

    return props.getAntennaInfo(props.antennaUuid);
});

const antennaReadings = computed(() => {
    if (!props.antennaUuid) {
        return props.readings;
    }

    return props.getAntennaReadings(props.antennaUuid);
});

const isAllAntennasMode = computed(() => !props.antennaUuid);
const antennaStats = computed(() => {
    const totalReadings = antennaReadings.value.length;
    const totalEpcs = antennaReadings.value.reduce((sum, reading) => sum + (reading.readings?.length || 0), 0);

    return {
        totalReadings: totalReadings,
        totalEpcs,
    };
});

watch(
    () => isOpen.value,
    async (newValue: boolean) => {
        if (newValue) {
            dialogStore.open(dialogId);
        } else {
            dialogStore.close(dialogId);
        }
    },
    { deep: true, immediate: true },
);

const handleCancel = () => {
    isOpen.value = false;
};
</script>

<template>
    <BDialog
        :id="dialogId"
        :max-width="800"
        :show-save-and-close="false"
        @cancel="handleCancel"
    >
        <template #header>
            <div
                v-if="!isAllAntennasMode"
                class="d-flex ga-3 align-center"
            >
                <div class="d-flex flex-column gr-0">
                    <span>{{ antenna?.reader_name }} - {{ antenna?.name }}</span>
                    <span class="text-body-2 text-grey-600"> #{{ antenna?.uuid }} </span>
                </div>
                <BChip
                    color="success"
                    prepend-icon="mdi-counter"
                    size="small"
                >
                    {{ antennaStats.totalReadings }} {{ t('common.readings') }}
                </BChip>
                <BChip
                    color="info"
                    prepend-icon="mdi-tag-multiple"
                    size="small"
                >
                    {{ antennaStats.totalEpcs }} EPC
                </BChip>
            </div>
        </template>

        <template #body>
            <LogList
                :readings="antennaReadings"
                :show-antenna-name="isAllAntennasMode"
                :max-epcs="10"
                :empty-message="isAllAntennasMode ? t('common.noActivity') : t('antennas.noReadings')"
                :empty-icon="isAllAntennasMode ? 'mdi-history' : 'mdi-antenna-off'"
                container-class="log-container"
                :get-antenna-name="uuid => getAntennaInfo(uuid)?.name || ''"
            />
        </template>

        <template #actions>
            <BBtn
                color="secondary"
                rounded
                class="btn-dialog-cta-action"
                @click="handleCancel"
            >
                {{ t('common.close') }}
            </BBtn>
        </template>
    </BDialog>
</template>

<style lang="scss" scoped>
.log-container {
    max-height: 500px;
    overflow-y: auto;
}
</style>
